import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/api/client";

// ─── Pool options ─────────────────────────────────────────────────────────
const POOLS = ["沪深300", "中证500", "中证1000", "创业板", "科创50", "北证50"] as const;
const REBALANCE_OPTIONS = [
  { value: "monthly", label: "每月" },
  { value: "weekly", label: "每周" },
  { value: "daily", label: "每日" },
] as const;

// ─── Helpers ───────────────────────────────────────────────────────────────

function fmtPct(v: number, digits = 2): string {
  if (v == null || isNaN(v)) return "--";
  return (v >= 0 ? "+" : "") + v.toFixed(digits) + "%";
}

function fmtNum(v: number): string {
  if (v == null || isNaN(v)) return "--";
  if (Math.abs(v) >= 10000) return (v / 10000).toFixed(2) + "万";
  if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + "k";
  return v.toFixed(0);
}

function defaultDate(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysAgo);
  return d.toISOString().slice(0, 10);
}

// ─── Chart component (simple SVG NAV curve) ────────────────────────────────

function NavChart({ points, width = 360, height = 180 }: {
  points: { date: string; nav: number }[];
  width?: number;
  height?: number;
}) {
  if (!points || points.length < 2) return null;

  const vals = points.map(p => p.nav);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const pad = 20;
  const chartW = width - pad * 2;
  const chartH = height - pad * 2;

  const xScale = (i: number) => pad + (i / (points.length - 1)) * chartW;
  const yScale = (v: number) => pad + chartH - ((v - min) / range) * chartH;

  const d = points.map((p, i) => `${i === 0 ? "M" : "L"}${xScale(i).toFixed(1)},${yScale(p.nav).toFixed(1)}`).join(" ");

  // Y-axis tick labels
  const yTicks = [min, (min + max) / 2, max];

  // X-axis labels — show first, middle, last
  const xLabels = [0, Math.floor(points.length / 2), points.length - 1].map(i => ({
    x: xScale(i),
    label: points[i]?.date?.slice(5) || "",
  }));

  const color = vals[vals.length - 1] >= vals[0] ? "#10B981" : "#E11D48";

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      {/* Y-axis grid lines */}
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={pad} y1={yScale(v)} x2={pad + chartW} y2={yScale(v)}
            stroke="#E5E7EB" strokeWidth="1" />
          <text x={pad - 4} y={yScale(v) + 3} textAnchor="end" className="fill-foreground-400"
            fontSize="9">{v.toFixed(3)}</text>
        </g>
      ))}
      {/* X-axis labels */}
      {xLabels.map((xl, i) => (
        <text key={i} x={xl.x} y={height - 2} textAnchor="middle" className="fill-foreground-400"
          fontSize="9">{xl.label}</text>
      ))}
      {/* NAV line */}
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      {/* Start/end dots */}
      <circle cx={xScale(0)} cy={yScale(vals[0])} r="2.5" fill={color} />
      <circle cx={xScale(points.length - 1)} cy={yScale(vals[vals.length - 1])} r="2.5" fill={color} />
    </svg>
  );
}

// ─── Monthly returns heatmap ───────────────────────────────────────────────

function MonthlyHeatmap({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data || {}).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return null;

  const maxAbs = Math.max(...entries.map(([, v]) => Math.abs(v)), 0.01);

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([month, val]) => {
        const pct = val / maxAbs;
        const isPos = val >= 0;
        // Color intensity based on magnitude
        const alpha = Math.min(Math.abs(pct) * 0.8 + 0.1, 0.9);
        const bg = isPos ? `rgba(16,185,129,${alpha})` : `rgba(225,29,72,${alpha})`;
        return (
          <div key={month} className="flex flex-col items-center" style={{ minWidth: 48 }}>
            <div className="text-[9px] text-foreground-400 mb-0.5">{month.slice(5)}</div>
            <div className="rounded-md px-2 py-1 text-[11px] font-semibold text-white text-center"
              style={{ backgroundColor: bg }}>
              {fmtPct(val, 1)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────

export default function AIStrategyBacktest() {
  const { id } = useParams();
  const navigate = useNavigate();
  const resultRef = useRef<HTMLDivElement>(null);

  // Strategy info
  const [strategy, setStrategy] = useState<any>(null);
  const [strategyDefaultPools, setStrategyDefaultPools] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Params
  const [startDate, setStartDate] = useState(defaultDate(-365));
  const [endDate, setEndDate] = useState(defaultDate(0));
  const [selectedPools, setSelectedPools] = useState<string[]>(["沪深300", "中证500"]);
  const [topN, setTopN] = useState(10);
  const [rebalance, setRebalance] = useState("monthly");
  const [fund, setFund] = useState(1000000);
  const [preset, setPreset] = useState("1y");

  // Result
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState<{
    done: number; total: number; date?: string; picks?: string[]; message?: string;
  } | null>(null);
  const [error, setError] = useState("");

  // Load strategy
  useEffect(() => {
    if (!id) return;
    api.get<any>(`/alpha-lab/strategies/${id}`)
      .then((d) => {
        if (d?.id) {
          setStrategy(d);
          // Default params from strategy
          const cfg = (() => { try { return JSON.parse(d.configParams || "{}"); } catch { return {}; } })();
          if (cfg.stockPool?.pools) {
            setSelectedPools(cfg.stockPool.pools);
            setStrategyDefaultPools(cfg.stockPool.pools);
          }
          if (cfg.stockPool?.maxHoldings) setTopN(cfg.stockPool.maxHoldings);
          if (d.holdingPeriodDays > 30) setRebalance("monthly");
          else if (d.holdingPeriodDays > 10) setRebalance("weekly");
          else setRebalance("daily");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Preset time ranges
  const presets = [
    { key: "3m", label: "近3月", days: -90 },
    { key: "6m", label: "近6月", days: -180 },
    { key: "1y", label: "近1年", days: -365 },
    { key: "3y", label: "近3年", days: -365 * 3 },
    { key: "5y", label: "近5年", days: -365 * 5 },
  ];

  const handlePreset = (key: string, days: number) => {
    setPreset(key);
    setStartDate(defaultDate(days));
    setEndDate(defaultDate(0));
  };

  const togglePool = (pool: string) => {
    setSelectedPools((prev) =>
      prev.includes(pool) ? prev.filter((p) => p !== pool) : [...prev, pool],
    );
  };

  const handleRun = () => {
    if (!id || running) return;
    setRunning(true);
    setError("");
    setResult(null);
    setProgress(null);

    const token =
      localStorage.getItem("alpha_token") ||
      localStorage.getItem("token") ||
      "";
    const params = new URLSearchParams();
    params.set("token", token);
    params.set("start", startDate);
    params.set("end", endDate);
    if (selectedPools.length > 0) params.set("pools", selectedPools.join(","));
    params.set("top_n", String(topN));
    params.set("rebalance", rebalance);
    params.set("fund", String(fund));

    const es = new EventSource(
      `/api/alpha-lab/strategies/${id}/backtest/stream?${params.toString()}`
    );

    es.addEventListener("start", (e: MessageEvent) => {
      try {
        const d = JSON.parse(e.data);
        setProgress({ done: 0, total: d.total || 0, message: d.message });
      } catch {
        // Ignore malformed progress events and keep the stream alive.
      }
    });

    es.addEventListener("log", (e: MessageEvent) => {
      try {
        const d = JSON.parse(e.data);
        setProgress((prev) => prev ? {
          ...prev,
          done: d.done ?? prev.done,
          date: d.date,
          message: d.message,
        } : prev);
      } catch {
        // Ignore malformed progress events and keep the stream alive.
      }
    });

    es.addEventListener("rebalance", (e: MessageEvent) => {
      try {
        const d = JSON.parse(e.data);
        setProgress((prev) => ({
          ...(prev || { total: d.total || 0 }),
          done: d.done ?? (prev?.done || 0) + 1,
          total: d.total || prev?.total || 0,
          date: d.date,
          picks: d.picks,
          message: `${d.date} → 选出 ${(d.picks || []).join(", ") || "无"} (${d.status})`,
        }));
      } catch {
        // Ignore malformed rebalance events and keep the stream alive.
      }
    });

    es.addEventListener("done", (e: MessageEvent) => {
      try {
        const d = JSON.parse(e.data);
        if (d.result) {
          setResult(d.result);
          setProgress(null);
          setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
        } else {
          setError("回测完成但未返回结果");
        }
      } catch {
        setError("结果解析失败");
      }
      es.close();
      setRunning(false);
    });

    es.addEventListener("error", (e: MessageEvent) => {
      let msg = "回测失败";
      try {
        if (e.data) {
          const d = JSON.parse(e.data);
          msg = d.error || msg;
        }
      } catch {
        // Fall back to the generic error message when the payload is invalid.
      }
      setError(msg);
      es.close();
      setRunning(false);
      setProgress(null);
    });

    es.onerror = () => {
      // EventSource doesn't expose HTTP status. If we're not already in a
      // terminal state (running===true), the stream closed unexpectedly.
      if (running) {
        setError("连接中断，请检查网络后重试");
        setRunning(false);
        setProgress(null);
        es.close();
      }
    };
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!strategy) {
    return (
      <div className="flex-1 px-4 py-4">
        <p className="text-foreground-400 text-sm text-center py-12">策略未找到</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)}
          className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-s-line text-foreground-600 text-lg" />
        </button>
        <h1 className="text-lg font-bold text-foreground-950">策略回测</h1>
      </div>

      {/* Strategy info card */}
      <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-base font-bold text-foreground-950">{strategy.name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 ml-2">
              {strategy.style}
            </span>
          </div>
        </div>
        <p className="text-[10px] text-foreground-400 mt-1">{strategy.tags}</p>
        <p className="text-xs text-foreground-600 mt-1 line-clamp-2">{strategy.description}</p>
      </div>

      {/* Parameter config */}
      <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4 space-y-4">
        <h3 className="text-sm font-semibold text-foreground-950">回测参数</h3>

        {/* Time range */}
        <div>
          <label className="text-[11px] font-medium text-foreground-500 mb-1.5 block">回测时间段</label>
          <div className="flex gap-1.5 mb-2 flex-wrap">
            {presets.map((p) => (
              <button key={p.key} onClick={() => handlePreset(p.key, p.days)}
                className={`text-[11px] px-2.5 py-1 rounded-full font-medium cursor-pointer transition-colors ${
                  preset === p.key ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"
                }`}>{p.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPreset(""); }}
              className="flex-1 px-2 py-1.5 rounded-lg border border-background-200 bg-white text-foreground-950 text-[11px]" />
            <span className="text-foreground-400">至</span>
            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPreset(""); }}
              className="flex-1 px-2 py-1.5 rounded-lg border border-background-200 bg-white text-foreground-950 text-[11px]" />
          </div>
        </div>

        {/* Stock pools */}
        <div>
          <label className="text-[11px] font-medium text-foreground-500 mb-1.5 block">股票池</label>
          <div className="flex gap-1.5 flex-wrap">
            {POOLS.map((pool) => {
              const isDefault = strategyDefaultPools.includes(pool);
              return (
                <button key={pool} onClick={() => togglePool(pool)}
                  className={`relative text-[11px] px-2.5 py-1 rounded-full font-medium cursor-pointer transition-colors ${
                    selectedPools.includes(pool)
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-500"
                  }`}>
                  {pool}
                  {isDefault && <span className="ml-0.5 text-[9px] opacity-80">策略推荐</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Holdings count */}
        <div>
          <label className="text-[11px] font-medium text-foreground-500 mb-1.5 block">
            同时持仓：{topN} 只
          </label>
          <input type="range" min="3" max="20" value={topN}
            onChange={(e) => setTopN(Number(e.target.value))}
            className="w-full accent-primary-500" />
        </div>

        {/* Rebalance */}
        <div>
          <label className="text-[11px] font-medium text-foreground-500 mb-1.5 block">调仓频率</label>
          <div className="flex gap-2">
            {REBALANCE_OPTIONS.map((opt) => (
              <button key={opt.value} onClick={() => setRebalance(opt.value)}
                className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer transition-colors ${
                  rebalance === opt.value
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-500"
                }`}>{opt.label}</button>
            ))}
          </div>
        </div>

        {/* Fund */}
        <div>
          <label className="text-[11px] font-medium text-foreground-500 mb-1.5 block">初始资金</label>
          <div className="flex items-center gap-2">
            <input type="number" value={fund} min={10000} step={50000}
              onChange={(e) => setFund(Number(e.target.value) || 1000000)}
              className="flex-1 px-2 py-1.5 rounded-lg border border-background-200 bg-white text-foreground-950 text-[11px]" />
            <span className="text-[11px] text-foreground-400">元</span>
          </div>
        </div>

        {/* LLM-only params notice */}
        {(() => {
          const scfg = (() => { try { return JSON.parse(strategy.configParams || "{}"); } catch { return {}; } })();
          const maxPos = scfg?.capital?.maxSinglePosPct;
          const tp = scfg?.risk?.takeProfitPct;
          const sl = scfg?.risk?.stopLossPct;
          if (!maxPos && !tp && !sl) return null;
          return (
            <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200/60">
              <p className="text-[10px] text-amber-700 leading-relaxed">
                <i className="ri-information-line mr-1" />
                以下策略参数在 AI 实盘运行中生效，回测暂未覆盖：
                {maxPos ? `单仓上限 ${maxPos}%` : ""}{maxPos && (tp || sl) ? "，" : ""}
                {tp ? `止盈 ${tp}%` : ""}{tp && sl ? "，" : ""}
                {sl ? `止损 ${sl}%` : ""}
              </p>
            </div>
          );
        })()}

        {/* Run button */}
        <button onClick={handleRun} disabled={running || selectedPools.length === 0}
          className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-primary-500 to-accent-500 text-white cursor-pointer disabled:opacity-50 transition-all shadow-sm">
          {running ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              回测计算中...
            </span>
          ) : (
            <><i className="ri-bar-chart-2-line mr-1" />开始回测</>
          )}
        </button>
      </div>

      {/* Progress — live SSE log stream */}
      {progress && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-foreground-950">
              {progress.total > 0
                ? `正在回测... (${progress.done}/${progress.total})`
                : "准备中..."}
            </span>
            {progress.date && (
              <span className="text-foreground-500">{progress.date}</span>
            )}
          </div>
          {/* Progress bar */}
          {progress.total > 0 && (
            <div className="h-1.5 bg-background-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%`,
                }}
              />
            </div>
          )}
          {/* Live log */}
          {progress.message && (
            <p className="text-[10px] text-foreground-500 leading-relaxed break-all">
              {progress.message}
            </p>
          )}
          {/* Selected picks */}
          {progress.picks && progress.picks.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {progress.picks.map((code) => (
                <span
                  key={code}
                  className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-primary-50 text-primary-600 font-medium"
                >
                  {code.replace(/^(sh|sz|bj)/, "")}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div ref={resultRef} className="space-y-4">
          {/* Metric cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-3 border border-background-200/60 text-center">
              <div className={`text-lg font-bold ${result.annual_return >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                {fmtPct(result.annual_return)}
              </div>
              <div className="text-[10px] text-foreground-400">年化收益</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-background-200/60 text-center">
              <div className="text-lg font-bold text-[#10B981]">{fmtPct(-result.max_drawdown, 2)}</div>
              <div className="text-[10px] text-foreground-400">最大回撤</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-background-200/60 text-center">
              <div className="text-lg font-bold text-foreground-950">{result.sharpe != null ? result.sharpe.toFixed(2) : "--"}</div>
              <div className="text-[10px] text-foreground-400">夏普比率</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-background-200/60 text-center">
              <div className="text-lg font-bold text-foreground-950">{fmtPct(result.win_rate, 1)}</div>
              <div className="text-[10px] text-foreground-400">胜率</div>
            </div>
          </div>

          {/* NAV chart */}
          {result.points && result.points.length >= 2 && (
            <div className="bg-white rounded-xl p-4 border border-background-200/60">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-foreground-950">净值曲线</h3>
                <div className="flex items-center gap-3 text-[10px]">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#10B981] inline-block" />
                    策略
                  </span>
                  {result.benchmark_return != null && (
                    <span className="flex items-center gap-1 text-foreground-400">
                      <span className="w-2 h-2 rounded-full bg-foreground-300 inline-block" />
                      基准
                    </span>
                  )}
                </div>
              </div>
              <NavChart points={result.points} />
            </div>
          )}

          {/* Benchmark comparison */}
          <div className="bg-white rounded-xl p-4 border border-background-200/60">
            <h3 className="text-xs font-semibold text-foreground-950 mb-2">基准对比</h3>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div>
                <span className="text-foreground-400">策略收益</span>
                <span className={`float-right font-semibold ${result.total_return >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                  {fmtPct(result.total_return)}
                </span>
              </div>
              {result.benchmark_return != null && (
                <div>
                  <span className="text-foreground-400">基准收益</span>
                  <span className="float-right font-semibold text-foreground-600">{fmtPct(result.benchmark_return)}</span>
                </div>
              )}
              {result.alpha != null && (
                <div>
                  <span className="text-foreground-400">Alpha</span>
                  <span className={`float-right font-semibold ${result.alpha >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                    {fmtPct(result.alpha)}
                  </span>
                </div>
              )}
              {result.beta != null && (
                <div>
                  <span className="text-foreground-400">Beta</span>
                  <span className="float-right font-semibold text-foreground-600">{result.beta.toFixed(2)}</span>
                </div>
              )}
              {result.sortino != null && (
                <div>
                  <span className="text-foreground-400">Sortino</span>
                  <span className="float-right font-semibold text-foreground-600">{result.sortino.toFixed(2)}</span>
                </div>
              )}
              {result.calmar != null && (
                <div>
                  <span className="text-foreground-400">Calmar</span>
                  <span className="float-right font-semibold text-foreground-600">{result.calmar.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Monthly returns heatmap */}
          {result.monthly_returns && Object.keys(result.monthly_returns).length > 0 && (
            <div className="bg-white rounded-xl p-4 border border-background-200/60">
              <h3 className="text-xs font-semibold text-foreground-950 mb-3">月度收益</h3>
              <MonthlyHeatmap data={result.monthly_returns} />
            </div>
          )}

          {/* Trading stats */}
          <div className="bg-white rounded-xl p-4 border border-background-200/60">
            <h3 className="text-xs font-semibold text-foreground-950 mb-2">交易统计</h3>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div>
                <span className="text-foreground-400">交易天数</span>
                <span className="float-right font-semibold">{result.trade_days ?? "--"}</span>
              </div>
              <div>
                <span className="text-foreground-400">调仓次数</span>
                <span className="float-right font-semibold">{result.rebalance_count ?? "--"}</span>
              </div>
              <div>
                <span className="text-foreground-400">换手率</span>
                <span className="float-right font-semibold">{fmtPct(result.turnover, 1)}</span>
              </div>
              <div>
                <span className="text-foreground-400">选股范围</span>
                <span className="float-right font-semibold">{result.universe_size || "--"} 只</span>
              </div>
              <div>
                <span className="text-foreground-400">交易成本</span>
                <span className="float-right font-semibold">{fmtPct(result.cost_pct, 2)}</span>
              </div>
              <div>
                <span className="text-foreground-400">费用合计</span>
                <span className="float-right font-semibold">{fmtNum(result.cost_paid)}</span>
              </div>
            </div>
          </div>

          {/* Re-run button */}
          <div className="text-center pb-4">
            <p className="text-[10px] text-foreground-400 mb-2">
              回测期间：{result.points?.[0]?.date?.slice(0, 10) || startDate} 至 {result.points?.[result.points.length - 1]?.date?.slice(0, 10) || endDate}
              &nbsp;|&nbsp;策略：{result.strategy || rebalance}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
